import { Api } from "laravel-request";

export type PsmRefactoredPortfoliosData = {
    account_numbers?: string[];
    accounts_csv?: string;
    script?: string;
    parser_base_url?: string;
    [key: string]: any;
};

export type RunPsmRefactoredPayload = {
    start?: string;
    end?: string;
    account_numbers?: string[];
    concurrent?: boolean;
    rate_limit?: boolean;
};

type LaravelRestResponse<T> = {
    data?: T;
    result?: string;
    message?: string;
    meta?: { message?: string; errors?: any };
};

function extractData<T>(response: LaravelRestResponse<T> | T | undefined): T | undefined {
    if (response == null || typeof response !== "object") {
        return response as T | undefined;
    }
    if ("data" in response && (response as LaravelRestResponse<T>).data !== undefined) {
        return (response as LaravelRestResponse<T>).data as T;
    }
    return response as T;
}

function errorMessage(responseData?: LaravelRestResponse<any>): string {
    if (!responseData) return "Ошибка запроса";
    return (
        responseData.message ||
        (responseData.meta?.message as string) ||
        (typeof responseData.meta?.errors === "string" ? responseData.meta.errors : undefined) ||
        "Ошибка запроса"
    );
}

export function fetchPsmRefactoredPortfolios(
    onSuccess: (data: PsmRefactoredPortfoliosData) => void,
    onError?: (message?: string) => void
) {
    Api.get("aton-client", "portfolios_list", { timestamp: Date.now() })
        .withoutNotify()
        .call(
            (response: LaravelRestResponse<PsmRefactoredPortfoliosData>) => {
                const data = extractData(response);
                if (data != null && typeof data === "object") {
                    onSuccess(data as PsmRefactoredPortfoliosData);
                } else {
                    onError?.("Пустой ответ");
                }
            },
            (_xhr: any, responseData?: LaravelRestResponse<any>) => {
                onError?.(errorMessage(responseData));
            }
        );
}

export function runPsmRefactoredJob(
    payload: RunPsmRefactoredPayload,
    onSuccess: (data: { job_id?: string | number }) => void,
    onError?: (message?: string) => void
) {
    const data: Record<string, any> = {};
    if (payload.start?.trim()) data.start = payload.start.trim();
    if (payload.end?.trim()) data.end = payload.end.trim();
    if (payload.account_numbers && payload.account_numbers.length > 0) {
        data.account_numbers = payload.account_numbers;
    }
    if (payload.concurrent === true) data.concurrent = true;
    if (payload.rate_limit === true) data.rate_limit = true;

    Api.post("aton-client", "run-psm-refactored", { data })
        .withoutNotify()
        .call(
            (response: LaravelRestResponse<{ job_id?: string | number }>) => {
                const inner = extractData(response);
                onSuccess(inner && typeof inner === "object" ? inner : { job_id: undefined });
            },
            (_xhr: any, responseData?: LaravelRestResponse<any>) => {
                onError?.(errorMessage(responseData));
            }
        );
}

function buildRunPsmPayloadData(payload: RunPsmRefactoredPayload): Record<string, any> {
    const data: Record<string, any> = {};
    if (payload.start?.trim()) data.start = payload.start.trim();
    if (payload.end?.trim()) data.end = payload.end.trim();
    if (payload.account_numbers && payload.account_numbers.length > 0) {
        data.account_numbers = payload.account_numbers;
    }
    if (payload.concurrent === true) data.concurrent = true;
    if (payload.rate_limit === true) data.rate_limit = true;
    return data;
}

function runPsmByMethod(
    method: "run-psm-new-ui-idle" | "run-psm-new-ui-send",
    payload: RunPsmRefactoredPayload,
    onSuccess: (data: { job_id?: string | number }) => void,
    onError?: (message?: string) => void
) {
    const data = buildRunPsmPayloadData(payload);
    Api.post("aton-client", method, { data })
        .withoutNotify()
        .call(
            (response: LaravelRestResponse<{ job_id?: string | number }>) => {
                const inner = extractData(response);
                onSuccess(inner && typeof inner === "object" ? inner : { job_id: undefined });
            },
            (_xhr: any, responseData?: LaravelRestResponse<any>) => {
                onError?.(errorMessage(responseData));
            }
        );
}

export function runPsmNewUiIdleJob(
    payload: RunPsmRefactoredPayload,
    onSuccess: (data: { job_id?: string | number }) => void,
    onError?: (message?: string) => void
) {
    runPsmByMethod("run-psm-new-ui-idle", payload, onSuccess, onError);
}

export function runPsmNewUiSendJob(
    payload: RunPsmRefactoredPayload,
    onSuccess: (data: { job_id?: string | number }) => void,
    onError?: (message?: string) => void
) {
    runPsmByMethod("run-psm-new-ui-send", payload, onSuccess, onError);
}

export function requestLegacyPsmPortfolioJob(onSuccess: (data: any) => void, onError?: (message?: string) => void) {
    Api.get("aton-client", "psm-portfolio-job", { timestamp: Date.now() })
        .withoutNotify()
        .call(
            (response: LaravelRestResponse<any>) => {
                onSuccess(extractData(response) ?? response);
            },
            (_xhr: any, responseData?: LaravelRestResponse<any>) => {
                onError?.(errorMessage(responseData));
            }
        );
}

export function parseAccountNumbersInput(raw: string): string[] {
    return raw
        .split(/[\s,;]+/u)
        .map((s) => s.trim())
        .filter(Boolean);
}

export function portfoliosToAccountsInputText(data: PsmRefactoredPortfoliosData | null): string {
    if (!data) return "";
    const nums = data.account_numbers;
    if (Array.isArray(nums) && nums.length > 0) {
        return nums
            .map((n) => String(n).trim())
            .filter(Boolean)
            .join(", ");
    }
    if (typeof data.accounts_csv === "string" && data.accounts_csv.trim()) {
        return parseAccountNumbersInput(data.accounts_csv).join(", ");
    }
    return "";
}
